import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { UserCheck, Loader2 } from 'lucide-react';

/**
 * "Did you hire {proName}?" confirmation dialog (W3). POSTs {hired: bool} to
 * /api/pro-contacts/{click_id}/confirm-hire. Handles 409 (already answered) gracefully
 * by treating it as done. onDone() is called after any resolved answer so the parent
 * can re-fetch.
 *
 * @param {object} contact - the as_client row ({ id, pro_name, ... }).
 * @param {boolean} open
 * @param {(open: boolean) => void} onOpenChange
 * @param {() => void} onDone - called after a successful/settled answer.
 */
const ConfirmHireDialog = ({ contact, open, onOpenChange, onDone }) => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [busyAnswer, setBusyAnswer] = useState(null); // true = yes, false = no

  const answer = async (hired) => {
    if (submitting || !contact?.id) return;
    setSubmitting(true);
    setBusyAnswer(hired);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error(t('services.loginRequired'));
        return;
      }
      await api.post(
        `/api/pro-contacts/${contact.id}/confirm-hire`,
        { hired },
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );
      toast.success(hired ? t('services.hireConfirmedYes') : t('services.hireConfirmedNo'));
      onOpenChange(false);
      if (onDone) onDone();
    } catch (err) {
      const status = err?.response?.status;
      const detail = typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : '';
      if (status === 409) {
        // Already answered elsewhere — treat as settled, refresh the list.
        toast.message(detail || t('services.hireAlreadyAnswered'));
        onOpenChange(false);
        if (onDone) onDone();
      } else if (status === 404) {
        toast.error(detail || t('services.hireNotYours'));
        onOpenChange(false);
        if (onDone) onDone();
      } else {
        toast.error(detail || t('services.errorGeneric'));
      }
    } finally {
      setSubmitting(false);
      setBusyAnswer(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!submitting) onOpenChange(next); }}>
      <DialogContent data-testid="confirm-hire-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCheck size={20} className="text-primary" aria-hidden="true" />
            {t('services.hireConfirmTitle', { name: contact?.pro_name || t('services.thisPro') })}
          </DialogTitle>
          <DialogDescription>{t('services.hireConfirmBody')}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            variant="outline"
            disabled={submitting}
            onClick={() => answer(false)}
            data-testid="confirm-hire-no-btn"
          >
            {submitting && busyAnswer === false && (
              <Loader2 size={16} className="mr-2 animate-spin" aria-hidden="true" />
            )}
            {t('services.hireConfirmNo')}
          </Button>
          <Button
            disabled={submitting}
            onClick={() => answer(true)}
            data-testid="confirm-hire-yes-btn"
          >
            {submitting && busyAnswer === true && (
              <Loader2 size={16} className="mr-2 animate-spin" aria-hidden="true" />
            )}
            {t('services.hireConfirmYes')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmHireDialog;
